import { useEffect, useRef, useState } from 'react'
import { ZoomIn } from 'lucide-react'
import { mediaManager } from '../../services/mediaManager'
import { useLivekitStore } from '../../stores/livekitStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { useVoiceStore } from '../../stores/voiceStore'
import { useAuthStore } from '../../stores/authStore'
import { useToastStore } from '../../stores/toastStore'
import { authApi } from '../../services/api'

const MIN_ZOOM = 0.5
const MAX_ZOOM = 3
const ZOOM_STEP = 0.05
const DEFAULT_ZOOM = 1
const MIN_PAN = -1
const MAX_PAN = 1
const PAN_STEP = 0.05
const DEFAULT_PAN = 0
/** Persist debounce — avoid spamming /me on every slider tick. */
const PERSIST_DELAY_MS = 600

interface PersistedFrame {
  zoom: number
  panX: number
  panY: number
}

function readPersisted(settings: any): PersistedFrame {
  const z = Number(settings?.cameraZoom)
  const px = Number(settings?.cameraPanX)
  const py = Number(settings?.cameraPanY)
  return {
    zoom: Number.isFinite(z) ? Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z)) : DEFAULT_ZOOM,
    panX: Number.isFinite(px) ? Math.max(MIN_PAN, Math.min(MAX_PAN, px)) : DEFAULT_PAN,
    panY: Number.isFinite(py) ? Math.max(MIN_PAN, Math.min(MAX_PAN, py)) : DEFAULT_PAN,
  }
}

/**
 * Lets the user reframe their own camera before publish — peers see
 * the chosen zoom + pan. Composes with the background-effect blur
 * (no longer mutually exclusive). Slider updates the running
 * processor live so changes appear instantly without a track restart.
 *
 * OSPROD-134: `compact` shrinks the trigger from the toolbar's 40px
 * disc to a 32px disc when this picker is rendered inside another
 * dropdown menu (VideoDeviceSelector's settings row). Behaviour
 * identical — only the trigger sizing changes.
 */
export default function CameraZoomPicker({ compact = false }: { compact?: boolean } = {}) {
  // Whichever room currently owns the local camera publish — voice
  // channel takes priority because the DM-call mutex (OSPROD-76)
  // tears the voice room down before bringing up a DM call, so at
  // most one is live. Reading both lets the same picker plug into
  // TelegramCallOverlay without duplicating the component.
  const channelRoom = useLivekitStore((s) => s.room)
  const dmRoom = useDmCallStore((s) => s.room)
  const room = channelRoom ?? dmRoom
  const user = useAuthStore((s) => s.user)
  const autoFaceCenter = useVoiceStore((s) => s.autoFaceCenter)
  const setAutoFaceCenter = useVoiceStore((s) => s.setAutoFaceCenter)
  const [open, setOpen] = useState(false)
  const initial = readPersisted(user?.settings)
  const [zoom, setZoom] = useState<number>(initial.zoom)
  const [panX, setPanX] = useState<number>(initial.panX)
  const [panY, setPanY] = useState<number>(initial.panY)
  const ref = useRef<HTMLDivElement>(null)
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Apply on mount + whenever the user's settings change (e.g. another
  // device updated the value). The initial publish also runs through
  // here because mediaManager re-applies after camera-on.
  useEffect(() => {
    const f = readPersisted(user?.settings)
    setZoom(f.zoom)
    setPanX(f.panX)
    setPanY(f.panY)
    // OSPROD-232: only (re)apply to the live pipeline when the persisted
    // frame actually differs from what mediaManager already has applied.
    // Merely MOUNTING this picker (e.g. opening the camera-settings menu,
    // where it renders in `compact` mode) used to call setVideoZoom/Pan
    // unconditionally → a needless CombinedProcessor rebuild → the video
    // flickered with "Empty video frame" / "Stream closed" every time the
    // menu opened. A genuine change (another device updated the setting,
    // or the initial camera-on) still differs and applies.
    const curZoom = mediaManager.getVideoZoom()
    const curPan = mediaManager.getVideoPan()
    if (Math.abs(curZoom - f.zoom) > 0.001) {
      mediaManager.setVideoZoom(room, f.zoom).catch(() => {})
    }
    if (Math.abs(curPan.x - f.panX) > 0.001 || Math.abs(curPan.y - f.panY) > 0.001) {
      mediaManager.setVideoPan(room, f.panX, f.panY).catch(() => {})
    }
  }, [room, user?.id])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // Cleanup any in-flight persist timer on unmount.
  useEffect(() => () => {
    if (persistTimer.current) clearTimeout(persistTimer.current)
  }, [])

  const persist = (next: PersistedFrame) => {
    if (persistTimer.current) clearTimeout(persistTimer.current)
    persistTimer.current = setTimeout(() => {
      const merged = {
        ...(user?.settings ?? {}),
        cameraZoom: next.zoom,
        cameraPanX: next.panX,
        cameraPanY: next.panY,
      }
      authApi.updateMe({ settings: merged } as any)
        .then((res) => useAuthStore.setState({ user: res.data }))
        .catch(() => {})
    }, PERSIST_DELAY_MS)
  }

  const handleErr = (err: any) => {
    useToastStore.getState().showToast({
      id: `zoom-fail-${Date.now()}`,
      type: 'error',
      title: "Couldn't apply zoom",
      body: err?.message ?? 'Unknown error',
    })
  }

  // Manual adjust takes the user out of auto-framing — otherwise the
  // BlazeFace tick would overwrite their setting on the next 3-second
  // detection. They can re-enable face centering from the ScanFace
  // button when they want it back. restoreFrame:false — the disable is
  // async and would otherwise land AFTER our setVideoZoom/setVideoPan and
  // restore the (stale) profile-saved frame over the value the user just
  // picked (OSPROD-232 "zoom resets right after I set it").
  const dropAutoFaceCenter = () => {
    if (autoFaceCenter) setAutoFaceCenter(false, { restoreFrame: false })
  }

  const applyZoom = (next: number) => {
    dropAutoFaceCenter()
    setZoom(next)
    mediaManager.setVideoZoom(room, next).catch(handleErr)
    persist({ zoom: next, panX, panY })
  }

  const applyPan = (nextX: number, nextY: number) => {
    dropAutoFaceCenter()
    setPanX(nextX)
    setPanY(nextY)
    mediaManager.setVideoPan(room, nextX, nextY).catch(handleErr)
    persist({ zoom, panX: nextX, panY: nextY })
  }

  const reset = () => {
    setZoom(DEFAULT_ZOOM)
    setPanX(DEFAULT_PAN)
    setPanY(DEFAULT_PAN)
    mediaManager.setVideoZoom(room, DEFAULT_ZOOM).catch(handleErr)
    mediaManager.setVideoPan(room, DEFAULT_PAN, DEFAULT_PAN).catch(handleErr)
    persist({ zoom: DEFAULT_ZOOM, panX: DEFAULT_PAN, panY: DEFAULT_PAN })
  }

  const isActive = Math.abs(zoom - DEFAULT_ZOOM) > 0.01 || panX !== 0 || panY !== 0
  // Pan only makes sense when zoom > 1 (cropped) or zoom < 1 (letterboxed).
  // At exactly 1 there's nothing to slide, so disable the controls.
  const panEnabled = Math.abs(zoom - 1) > 0.01

  const sz = compact ? 'w-8 h-8' : 'w-10 h-10'
  const iconSize = compact ? 14 : 20
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={isActive ? `Zoom ${zoom.toFixed(2)}× — click to adjust` : 'Camera zoom'}
        className={`${sz} rounded-full flex items-center justify-center transition duration-150 ${
          isActive ? 'bg-blue-600/80 text-white hover:bg-blue-600' : 'bg-muted text-foreground hover:bg-muted/80'
        }`}
      >
        <ZoomIn size={iconSize} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 right-0 w-72 bg-card rounded-lg ring-1 ring-border shadow-lg overflow-hidden z-30">
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border flex items-center justify-between">
            <span>Camera zoom</span>
            <span className="text-foreground normal-case">{zoom.toFixed(2)}×</span>
          </div>
          <div className="p-3 space-y-3">
            <div>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={ZOOM_STEP}
                value={zoom}
                onChange={(e) => applyZoom(Number(e.target.value))}
                className="w-full accent-primary"
                aria-label="Camera zoom level"
              />
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>0.5× wide</span>
                <span>1× normal</span>
                <span>3× close</span>
              </div>
            </div>

            {/* Pan offsets — Horizontal + Vertical sliders. Each slides
                the visible window inside the cropped/letterboxed frame. */}
            <div className={`space-y-2 ${panEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>Horizontal</span>
                <span className="text-foreground normal-case">{panX > 0 ? '+' : ''}{panX.toFixed(2)}</span>
              </div>
              <input
                type="range"
                min={MIN_PAN}
                max={MAX_PAN}
                step={PAN_STEP}
                value={panX}
                onChange={(e) => applyPan(Number(e.target.value), panY)}
                className="w-full accent-primary"
                aria-label="Horizontal pan"
                disabled={!panEnabled}
              />
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>Vertical</span>
                <span className="text-foreground normal-case">{panY > 0 ? '+' : ''}{panY.toFixed(2)}</span>
              </div>
              <input
                type="range"
                min={MIN_PAN}
                max={MAX_PAN}
                step={PAN_STEP}
                value={panY}
                onChange={(e) => applyPan(panX, Number(e.target.value))}
                className="w-full accent-primary"
                aria-label="Vertical pan"
                disabled={!panEnabled}
              />
            </div>

            <button
              onClick={reset}
              className="w-full text-xs py-1.5 rounded-md bg-muted hover:bg-accent text-foreground transition"
            >
              Reset
            </button>
          </div>
          <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
            Crops & shifts locally — peers see the framed video. Composes with background blur.
          </div>
        </div>
      )}
    </div>
  )
}
